import { useState } from 'react';
import { useDeckDocumentTitle } from '@/lib/use-deck-document-title';
import { useParams } from 'react-router-dom';
import { useCompanyConfig } from '@/lib/use-company-config';
import DeckShell from '@/components/shared/DeckShell';
import CoverSlide from '@/components/proposal/CoverSlide';
import ExecutiveSummarySlide from '@/components/proposal/ExecutiveSummarySlide';
import CompanySnapshotSlide from '@/components/proposal/CompanySnapshotSlide';
import LandscapePreviewSlide from '@/components/proposal/LandscapePreviewSlide';
import DeliverablesSlide from '@/components/proposal/DeliverablesSlide';
import type { ViewMode } from '@/types/roadmap.types';
import { CompanyConfigPlaceholder } from '@/components/shared/CompanyConfigPlaceholder';

export default function ProposalPresentation() {
  const { companyId } = useParams<{ companyId: string }>();
  const [mode, setMode] = useState<ViewMode>('presentation');
  const { config, loading, error } = useCompanyConfig(companyId);

  useDeckDocumentTitle(config?.company?.name);

  if (loading || error || !config) {
    return (
      <CompanyConfigPlaceholder companyId={companyId} loading={loading} error={error} config={config} />
    );
  }

  const slides = [
    {
      key: 'cover',
      label: 'Cover',
      component: <CoverSlide config={config} mode={mode} />,
    },
    {
      key: 'executive-summary',
      label: 'Executive Summary',
      component: <ExecutiveSummarySlide config={config} mode={mode} />,
    },
    {
      key: 'company-snapshot',
      label: 'Company Snapshot',
      component: <CompanySnapshotSlide config={config} mode={mode} />,
    },
    {
      key: 'landscape',
      label: 'Preliminary Landscape',
      component: <LandscapePreviewSlide config={config} mode={mode} />,
    },
    {
      key: 'deliverables',
      label: 'Deliverables',
      component: <DeliverablesSlide mode={mode} />,
    },
  ];

  return (
    <DeckShell
      slides={slides}
      title="Proposal"
      subtitle={config.company.name}
      altLink={{
        to: `/${config.id}/roadmap`,
        label: 'View Roadmap →',
      }}
      mode={mode}
      onModeChange={setMode}
      clientBrand={config.clientBrand}
      whiteLabel={config.whiteLabel}
    />
  );
}
